import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ProductThumbnail,
  Text
} from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';

import { formatPrice, useGiftCardData } from '../../hooks';
import {
  setIsGiftCardIncompatibleProductsModalOpen,
  setIsGiftCardModalRemoveOpen
} from '../../store/slices/billing';
import { useMediaQuery } from '../../ui/react/src';
import { Feedback, FeedbackType } from '../alert';

export default function GiftcardIncompatibleProductsModal() {
  const { isGiftCartIncompatibleProductsModalOpen } = useAppSelector(
    (state) => state.billing
  );

  const { getProductsNotCompatibleWithGiftCard } = useGiftCardData();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery('md');

  const productsNotCompatible = getProductsNotCompatibleWithGiftCard();

  const onClose = () => {
    dispatch(setIsGiftCardIncompatibleProductsModalOpen(false));
  };

  const onRemoveGiftCard = () => {
    onClose();
    dispatch(setIsGiftCardModalRemoveOpen(true));
  };

  return (
    <Modal
      isOpen={isGiftCartIncompatibleProductsModalOpen}
      onClose={onClose}
      size="md"
    >
      <ModalHeader
        icon={{
          fill: 'warning-500',
          name: 'warning-circle'
        }}
        onClose={onClose}
        title="Combina tu Gift Card"
      />
      <ModalBody className="co-flex co-flex-col co-gap-16px">
        <Feedback
          type={FeedbackType.WARNING}
          subtitle="Elige un medio de pago adicional o guarda estos productos para después."
        />
        <Text
          variant="body1"
          element="p"
          weight={600}
          color="neutral-700"
          className="co-leading-24px"
        >
          Gift Card no aplicable a estos productos:
        </Text>

        <div className="co-flex co-flex-col co-gap-16px co-overflow-y-auto">
          {productsNotCompatible.map((product) => (
            <ProductLineItem key={product.id} product={product} />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <div
          className="ui-flex ui-w-full ui-items-center ui-justify-end ui-gap-2"
          style={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}
        >
          <Button
            feedback="error"
            variant="secondary"
            block={isMobile}
            onClick={onRemoveGiftCard}
          >
            Eliminar Gift Card
          </Button>
          <Button variant="primary" block={isMobile} onClick={onClose}>
            Elegir otro medio de pago
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

const ProductLineItem = ({ product }: { product: CartLineItem }) => {
  const formattedPrice = formatPrice(product.price.value.centAmount);

  return (
    <div className="co-flex co-gap-8px">
      <ProductThumbnail
        alt="Paris Product Thumbnail"
        src={product?.variant?.images[0]?.url}
      />
      <div className="co-w-full co-flex co-justify-between">
        <div className="co-flex co-flex-col ">
          <Text
            variant="body2"
            element="p"
            weight={600}
            className="co-leading-[20px]"
          >
            {product.name}
          </Text>

          <Text variant="caption" element="p" className="co-leading-16px">
            Cantidad: {product.quantity}
          </Text>
        </div>

        <div className="co-flex co-flex-col">
          <Text
            variant="body2"
            element="p"
            weight={500}
            className="co-leading-[20px]"
          >
            {formattedPrice}
          </Text>
        </div>
      </div>
    </div>
  );
};
